import { useState } from "react";

import Button from "components/Button";
import HollowButton from "components/HollowButton";
import SelectTemplate from "components/SelectTemplate";

import localLawService from "api/localLawService";
import { IDossierItem } from "dto/Dossier/IDossierItem";
import { ITemplate } from "dto/ITemplate";
import { ELegalActType, ILegalActType } from "dto/LocalLaw/ILocalLaw";
import { ENotificationType, notification } from "utils/notification";

import styles from "./styles.module.scss";

interface Props {
  types: ILegalActType[];
  onClose: () => void;
  /** Zwraca Id dokumentu edytora — od razu otwieramy go w Wordzie. */
  onCreated: (documentId: string) => void;
}

// Nowy projekt aktu prawa miejscowego — odpowiednik okna zakładania dokumentu
// z listy „Projekty aktów" w Magistracie.
//
// Zakładamy tu wyłącznie metrykę i pusty dokument; treść powstaje w Wordzie.
// Dokument NIE dostaje klucza obiektu — treść aktu jest jawna.

const errorText = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { Message?: string } } })?.response?.data?.Message ??
  fallback;

const today = () => new Date().toISOString().slice(0, 10);

const NewActModal = ({ types, onClose, onCreated }: Props) => {
  const [actType, setActType] = useState<number>(ELegalActType.Uchwala);
  const [title, setTitle] = useState("");
  const [actNumber, setActNumber] = useState("");
  const [author, setAuthor] = useState("");
  const [date, setDate] = useState(today());
  const [template, setTemplate] = useState<IDossierItem | ITemplate | null>(null);

  const { mutate: createAct, isLoading } = localLawService.useCreateAct();

  const canApply = !!title.trim();

  const submit = () => {
    if (!canApply) return;

    createAct(
      {
        ActType: actType,
        Title: title.trim(),
        ActNumber: actNumber.trim() || null,
        Author: author.trim() || null,
        Date: date || null,
        TemplateId: template
          ? (template as IDossierItem).DossierId ?? template.Id
          : null,
      },
      {
        onSuccess: (response) => onCreated(response.data.DocumentId),
        onError: (e) =>
          notification({
            type: ENotificationType.ERROR,
            title: "Błąd",
            text: errorText(e, "Nie udało się założyć projektu aktu."),
          }),
      },
    );
  };

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={`${styles.modal} ${styles.modalWide}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={styles.modalTitle}>Nowy projekt aktu prawa miejscowego</div>

        <div className={styles.modalBody}>
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Rodzaj aktu</label>
              <select
                className={styles.textInput}
                value={actType}
                onChange={(e) => setActType(Number(e.target.value))}
              >
                {types.map((t) => (
                  <option key={t.Value} value={t.Value}>
                    {t.Name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>
                Numer (można nadać przy uchwaleniu)
              </label>
              <input
                className={styles.textInput}
                value={actNumber}
                placeholder="np. XIV/122/2026"
                onChange={(e) => setActNumber(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Przedmiot regulacji</label>
            <textarea
              className={styles.textArea}
              value={title}
              autoFocus
              placeholder="w sprawie ..."
              onChange={(e) => setTitle(e.target.value)}
            />
            <span className={styles.hint}>
              Fraza trafia do nagłówka aktu i do metryki XML jako przedmiot regulacji.
            </span>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Organ wydający (dopełniacz)</label>
              <input
                className={styles.textInput}
                value={author}
                placeholder="np. Rady Miejskiej w ..."
                onChange={(e) => setAuthor(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.fieldLabel}>Data aktu</label>
              <input
                className={styles.textInput}
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>Szablon (opcjonalnie)</label>
            <SelectTemplate
              isExternal={false}
              selectedTemplate={template}
              onChoose={(t) => setTemplate(t)}
            />
            <span className={styles.hint}>
              Bez wskazania szablonu projekt zaczyna się od szkieletu aktu wybranego
              rodzaju: nagłówek metrykalny, podstawa prawna, pierwsza jednostka redakcyjna
              i blok podpisów. Szkielety ma uchwała, zarządzenie i porozumienie —
              pozostałe rodzaje startują pustym dokumentem.
            </span>
          </div>

          <span className={styles.hint}>
            Po założeniu projekt otworzy się w edytorze Word — tam powstaje treść aktu,
            jednostki redakcyjne i XML do publikacji.
          </span>
        </div>

        <div className={styles.modalFooter}>
          <HollowButton text="Anuluj" onClick={onClose} />
          <Button
            text="Utwórz i otwórz"
            loading={isLoading}
            disabled={!canApply}
            onClick={submit}
          />
        </div>
      </div>
    </div>
  );
};

export default NewActModal;
